import 'client-only';

import { SentryFeedbackProvider } from './providers/sentry-feedback-provider.client';
import type {
  FeedbackPayload,
  FeedbackProvider,
} from './types/feedback-provider';

/**
 * Active client-side feedback providers.
 *
 * To add a destination:
 *   1. Implement FeedbackProvider in `./providers/<name>-feedback-provider.client.ts`
 *   2. Import it here and add an instance to this array.
 */
const providers: FeedbackProvider[] = [new SentryFeedbackProvider()];

/**
 * Submits a feedback payload to every registered provider in parallel.
 * Resolves if at least one provider accepted the feedback; rejects with
 * an aggregated error when every provider failed so the UI can render
 * a "couldn't send" state.
 */
export async function submitFeedback(payload: FeedbackPayload): Promise<void> {
  const results = await Promise.allSettled(
    providers.map(p => p.submit(payload))
  );

  const anySettledFulfilled = results.some(r => r.status === 'fulfilled');
  if (anySettledFulfilled) return;

  const reasons = results
    .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
    .map(r =>
      r.reason instanceof Error ? r.reason.message : String(r.reason)
    );
  throw new Error(`Feedback delivery failed: ${reasons.join('; ')}`);
}
